// Staff-only upload flow for historical / archive pancake pieces.
// Creates an order doc with _isHistorical:true and state:"done" so it flows
// into the existing gallery + overlay + insights pipelines automatically.
// AI description + BG removal reuse the standard generatePancakeDescription
// Cloud Function — no new backend.

import { useEffect, useRef, useState } from "react";
import { addDoc, collection, doc, getDocs, query, serverTimestamp, setDoc, Timestamp, where, writeBatch } from "firebase/firestore";
import { getDownloadURL, ref as storageRef, uploadBytes } from "firebase/storage";
import { getApp } from "firebase/app";
import { STAFF_THEME, FONT } from "./theme/staff";
import { getFunctions, httpsCallable } from "firebase/functions";
import { db, storage } from "./firebase";
import PreCropTool from "./components/PreCropTool";

// Synthetic container shift IDs. Kept as exports so other views can reference them.
export const HISTORICAL_SHIFT_ID = "historical_archive";
export const ORPHAN_SHIFT_ID     = "orphaned_orders";

const HISTORICAL_CONTAINER = {
  eventName: "🏛 Historical Archive",
  isActive: false,
  closedAt: Timestamp.fromDate(new Date(1970, 0, 1)),
  totalOrders: 0, // updated by migration
  _isHistoricalContainer: true,
};

const ORPHAN_CONTAINER = {
  eventName: "🧹 Orphaned Orders (test data, legacy)",
  isActive: false,
  closedAt: Timestamp.fromDate(new Date(1970, 0, 2)), // sorts just above historical
  totalOrders: 0,
  _isOrphanContainer: true,
};

// Upserts a shift container into BOTH `shifts` and `shift_summaries` collections.
// The shift archive view queries `shift_summaries`, so the summary copy is what
// makes the container visible. The `shifts` copy is for completeness and any
// code that still queries there.
async function upsertContainerShift(id, body) {
  await setDoc(doc(db, "shifts",          id), body, { merge: true });
  await setDoc(doc(db, "shift_summaries", id), body, { merge: true });
}

// Ensures both container shifts exist + sweeps null-shiftId orders into them.
// Historical uploads (`_isHistorical: true`) go to HISTORICAL_SHIFT_ID.
// Everything else goes to ORPHAN_SHIFT_ID.
// Idempotent — safe to call from multiple mount points.
export async function ensureContainerShifts() {
  await Promise.all([
    upsertContainerShift(HISTORICAL_SHIFT_ID, HISTORICAL_CONTAINER),
    upsertContainerShift(ORPHAN_SHIFT_ID,     ORPHAN_CONTAINER),
  ]);

  // Sweep: any order with shiftId == null goes somewhere.
  const orphanSnap = await getDocs(query(
    collection(db, "orders"),
    where("shiftId", "==", null),
  ));
  if (orphanSnap.empty) return;

  const batch = writeBatch(db);
  let histCount = 0, orphCount = 0;
  orphanSnap.docs.forEach((d) => {
    // Leave trashed orders alone — they're on their way to being purged.
    if (d.data().trashedAt) return;
    const target = d.data()._isHistorical === true ? HISTORICAL_SHIFT_ID : ORPHAN_SHIFT_ID;
    batch.update(d.ref, { shiftId: target });
    if (target === HISTORICAL_SHIFT_ID) histCount++; else orphCount++;
  });
  await batch.commit();
  console.log(`Migrated ${histCount} → historical, ${orphCount} → orphans`);
}

// Back-compat wrapper — existing callers still use this name.
async function ensureHistoricalContainer() {
  return ensureContainerShifts();
}

const BRAND = STAFF_THEME;

const MAX_EDGE = 1920;
const JPEG_QUALITY = 0.85;

async function compressImage(file) {
  const img = await new Promise((resolve, reject) => {
    const i = new Image();
    i.onload = () => resolve(i);
    i.onerror = reject;
    i.src = URL.createObjectURL(file);
  });
  const scale = Math.min(1, MAX_EDGE / Math.max(img.width, img.height));
  const w = Math.round(img.width * scale);
  const h = Math.round(img.height * scale);
  const canvas = document.createElement("canvas");
  canvas.width = w; canvas.height = h;
  canvas.getContext("2d").drawImage(img, 0, 0, w, h);
  URL.revokeObjectURL(img.src);
  return new Promise((resolve) => canvas.toBlob((b) => resolve(b), "image/jpeg", JPEG_QUALITY));
}

const VIBES_OPTIONS = [
  { id: "fire",    label: "🔥 Fire" },
  { id: "clean",   label: "🎯 Clean" },
  { id: "magic",   label: "✨ Magic" },
  { id: "lab",     label: "🧪 Lab" },
  { id: "grind",   label: "💪 Grind" },
  { id: "rough",   label: "🫠 Rough" },
  { id: "cute",    label: "🥰 Cute" },
  { id: "funny",   label: "😂 Funny" },
];

const COLOR_PALETTE = [
  "#1a1a1a", "#e74c3c", "#f39c12", "#f1c40f",
  "#27ae60", "#3498db", "#9b59b6", "#e91e63",
  "#ffffff", "#a0522d",
];

export default function HistoricalUploadModal({ onClose, onCreated }) {
  const [file, setFile]             = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [title, setTitle]           = useState("");
  const [approxDate, setApproxDate] = useState("");
  const [artistName, setArtistName] = useState(() => {
    try { return localStorage.getItem("gt_active_artist") || "Dan"; } catch { return "Dan"; }
  });
  const [hours,   setHours]   = useState("");
  const [minutes, setMinutes] = useState("");
  const [notes,   setNotes]   = useState("");
  const [colors,  setColors]  = useState([]);
  const [vibes,   setVibes]   = useState([]);
  const [aiOn,    setAiOn]    = useState(true);

  const [phase, setPhase] = useState("input"); // input | uploading | ai | done
  const [error, setError] = useState(null);
  const [showCrop, setShowCrop] = useState(false);
  const fileInputRef = useRef();

  useEffect(() => {
    return () => { if (previewUrl) URL.revokeObjectURL(previewUrl); };
  }, [previewUrl]);

  // Ensure container shift exists + migrate legacy orphans on first open.
  useEffect(() => {
    ensureHistoricalContainer().catch((err) => {
      console.warn("Historical container setup failed:", err.message);
    });
  }, []);

  const onPick = (f) => {
    if (!f) return;
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setFile(f);
    setPreviewUrl(URL.createObjectURL(f));
  };

  const toggleColor = (c) => {
    setColors((prev) => {
      if (prev.includes(c)) return prev.filter((x) => x !== c);
      if (prev.length >= 3) return prev;
      return [...prev, c];
    });
  };
  const toggleVibe = (v) => {
    setVibes((prev) => prev.includes(v) ? prev.filter((x) => x !== v) : [...prev, v]);
  };

  const canSubmit = file && title.trim() && approxDate && artistName.trim() && phase === "input";

  const onSubmit = async () => {
    if (!canSubmit) return;
    setError(null);
    setPhase("uploading");
    try {
      // Synthetic token so this behaves like an order without a real ticket
      const tokenId = "hist_" + Date.now().toString(36) + "_" + Math.random().toString(36).slice(2, 8);

      // 1. Compress + upload photo
      const blob = await compressImage(file);
      const path = `orders/${tokenId}/${Date.now()}_hist.jpg`;
      const r = storageRef(storage, path);
      await uploadBytes(r, blob, { contentType: "image/jpeg" });
      const photoUrl = await getDownloadURL(r);

      // 2. Compute estimated total time ms
      const h = parseFloat(hours) || 0;
      const m = parseFloat(minutes) || 0;
      const estimatedTotalTimeMs = (h * 3600 + m * 60) * 1000 || null;

      // 3. Parse approximate date → Timestamp at noon local
      const approxDt = new Date(approxDate + "T12:00:00");
      const completedAt = isNaN(approxDt) ? Timestamp.now() : Timestamp.fromDate(approxDt);

      // 4. Create order doc — state:"done" means it flows into gallery/insights
      // shiftId points to the synthetic historical container shift so it
      // appears in /archive alongside real shifts for deletion + editing.
      await ensureHistoricalContainer().catch(() => {}); // idempotent guard
      const orderPayload = {
        tokenId,
        shiftId:            HISTORICAL_SHIFT_ID,
        state:              "done",
        _isHistorical:      true,
        orderType:          "custom",
        subjectName:        title.trim(),
        recipientName:      title.trim(),
        artistName:         artistName.trim(),
        notes:              notes.trim() || null,
        favoriteColors:     colors.length > 0 ? colors : null,
        artistTagsReflection: vibes.length > 0 ? vibes : null,
        estimatedTotalTimeMs,
        photo_url:          photoUrl,
        completedAt,
        deliveredAt:        completedAt,
        createdAt:          serverTimestamp(),
        updatedAt:          serverTimestamp(),
      };
      const ref = await addDoc(collection(db, "orders"), orderPayload);

      // 5. (Optional) Run AI description — also generates cutout + tags
      if (aiOn) {
        setPhase("ai");
        try {
          const fn = httpsCallable(getFunctions(getApp()), "generatePancakeDescription");
          await fn({
            orderId:       ref.id,
            photoUrl,
            tokenId,
            subjectName:   title.trim(),
            recipientName: title.trim(),
            notes:         notes.trim() || null,
          });
        } catch (aiErr) {
          // AI failure shouldn't block the upload — log and move on
          console.warn("Historical AI description failed (non-fatal):", aiErr);
        }
      }

      setPhase("done");
      setTimeout(() => {
        onCreated?.(ref.id);
        onClose();
      }, 900);
    } catch (err) {
      console.error("Historical upload failed:", err);
      setError(err.message || "Upload failed — try again.");
      setPhase("input");
    }
  };

  const working = phase !== "input" && phase !== "done";

  return (
    <div
      onClick={working ? undefined : onClose}
      style={{
        position: "fixed", inset: 0, zIndex: 60,
        background: "rgba(0,0,0,0.75)",
        display: "flex", alignItems: "flex-start", justifyContent: "center",
        padding: "2rem 1rem", overflowY: "auto",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: BRAND.dark, border: `1px solid ${BRAND.border}`,
          borderRadius: "14px", padding: "1.1rem",
          maxWidth: "560px", width: "100%",
          color: "white", fontFamily: FONT.body,
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.9rem" }}>
          <h2 style={{ margin: 0, fontSize: "1.15rem", color: BRAND.gold }}>🏛 Upload historical piece</h2>
          <button
            type="button" onClick={onClose} disabled={working}
            style={{ background: "transparent", border: "none", color: BRAND.muted, fontSize: "1.5rem", cursor: working ? "default" : "pointer", lineHeight: 1 }}
          >×</button>
        </div>

        {error && (
          <div style={{ background: "#6b2234", color: "white", padding: "0.6rem 0.8rem", borderRadius: "8px", marginBottom: "0.7rem", fontSize: "0.85rem" }}>
            {error}
          </div>
        )}

        {/* Photo */}
        <L label="Photo *">
          {previewUrl ? (
            <div style={{ position: "relative" }}>
              <img src={previewUrl} alt="preview" style={{ width: "100%", maxHeight: "320px", objectFit: "contain", background: "#000", borderRadius: "8px" }} />
              {!working && (
                <div style={{ position: "absolute", top: "0.5rem", right: "0.5rem", display: "flex", gap: "0.4rem" }}>
                  <button type="button" onClick={() => setShowCrop(true)}
                    style={{ background: "rgba(252,179,31,0.85)", color: "#1a0a12", border: "none", borderRadius: "999px", padding: "0.3rem 0.7rem", fontSize: "0.78rem", fontWeight: 700, cursor: "pointer" }}>
                    📐 Crop
                  </button>
                  <button type="button" onClick={() => { setFile(null); setPreviewUrl(null); }}
                    style={{ background: "rgba(0,0,0,0.65)", color: "white", border: "none", borderRadius: "999px", padding: "0.3rem 0.7rem", fontSize: "0.78rem", cursor: "pointer" }}>
                    Remove
                  </button>
                </div>
              )}
            </div>
          ) : (
            <button
              type="button" onClick={() => fileInputRef.current?.click()}
              style={{ width: "100%", background: "rgba(252,179,31,0.1)", border: `2px dashed ${BRAND.gold}`, color: BRAND.gold, borderRadius: "10px", padding: "1.6rem", fontSize: "0.92rem", fontWeight: 700, cursor: "pointer" }}
            >
              📷 Choose a photo
            </button>
          )}
          <input ref={fileInputRef} type="file" accept="image/*" style={{ display: "none" }}
            onChange={(e) => { onPick(e.target.files?.[0]); e.target.value = ""; }} />
        </L>

        {showCrop && previewUrl && (
          <PreCropTool
            src={previewUrl}
            title="Crop the pancake"
            subtitle="Tighter framing means a cleaner cutout."
            onConfirm={(blob) => {
              setShowCrop(false);
              if (!blob) return;
              const f = new File([blob], (file?.name || "cropped") + ".jpg", { type: "image/jpeg" });
              onPick(f);
            }}
            onCancel={() => setShowCrop(false)}
          />
        )}

        <Grid2>
          <L label="Title / subject *">
            <input type="text" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Abuela's portrait" style={inputStyle} />
          </L>
          <L label="Approximate date *">
            <input type="date" value={approxDate} onChange={(e) => setApproxDate(e.target.value)} style={inputStyle} />
          </L>
        </Grid2>

        <Grid2>
          <L label="Artist *">
            <input type="text" value={artistName} onChange={(e) => setArtistName(e.target.value)} style={inputStyle} />
          </L>
          <L label="Estimated total time (optional)">
            <div style={{ display: "flex", gap: "0.4rem" }}>
              <input type="number" min="0" value={hours} onChange={(e) => setHours(e.target.value)} placeholder="h" style={{ ...inputStyle, flex: 1 }} />
              <input type="number" min="0" max="59" value={minutes} onChange={(e) => setMinutes(e.target.value)} placeholder="m" style={{ ...inputStyle, flex: 1 }} />
            </div>
          </L>
        </Grid2>

        <L label="Context note (optional)">
          <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2}
            placeholder="e.g. Made for Alice's 30th birthday. Featured in STL Magazine."
            style={{ ...inputStyle, resize: "vertical", fontFamily: "inherit" }} />
        </L>

        <L label={`Favorite colors (optional, up to 3) · ${colors.length}/3`}>
          <div style={{ display: "flex", flexWrap: "wrap", gap: "0.3rem" }}>
            {COLOR_PALETTE.map((c) => {
              const on = colors.includes(c);
              return (
                <button key={c} type="button" onClick={() => toggleColor(c)} aria-label={c}
                  style={{
                    width: "28px", height: "28px",
                    background: c, borderRadius: "999px",
                    border: on ? `3px solid ${BRAND.gold}` : `2px solid rgba(255,255,255,0.2)`,
                    cursor: "pointer",
                  }}
                />
              );
            })}
          </div>
        </L>

        <L label="Vibes (optional)">
          <div style={{ display: "flex", flexWrap: "wrap", gap: "0.3rem" }}>
            {VIBES_OPTIONS.map((v) => {
              const on = vibes.includes(v.id);
              return (
                <button key={v.id} type="button" onClick={() => toggleVibe(v.id)}
                  style={{
                    background: on ? BRAND.pink : "transparent",
                    color: on ? "white" : BRAND.cream,
                    border: `1px solid ${on ? BRAND.pink : BRAND.border}`,
                    borderRadius: "999px",
                    padding: "0.3rem 0.65rem",
                    fontSize: "0.8rem", fontWeight: 700, cursor: "pointer",
                  }}
                >{v.label}</button>
              );
            })}
          </div>
        </L>

        <label style={{ display: "flex", alignItems: "center", gap: "0.5rem", color: BRAND.cream, fontSize: "0.88rem", margin: "0.6rem 0" }}>
          <input type="checkbox" checked={aiOn} onChange={(e) => setAiOn(e.target.checked)} />
          ✨ Generate AI description, tags, and cutout (recommended)
        </label>

        {phase !== "input" && (
          <div style={{ background: BRAND.panel, border: `1px solid ${BRAND.border}`, borderRadius: "10px", padding: "0.7rem", margin: "0.6rem 0" }}>
            <div style={{ color: phase === "done" ? BRAND.green : BRAND.gold, fontWeight: 700, fontSize: "0.88rem" }}>
              {phase === "uploading" && "📤 Uploading photo…"}
              {phase === "ai"        && "🤖 Running AI description + cutout (this can take 30-60s)…"}
              {phase === "done"      && "✓ Added to the gallery!"}
            </div>
          </div>
        )}

        <div style={{ display: "flex", justifyContent: "flex-end", gap: "0.5rem", marginTop: "0.8rem" }}>
          <button type="button" onClick={onClose} disabled={working}
            style={{ background: "transparent", color: BRAND.cream, border: `1px solid ${BRAND.border}`, borderRadius: "8px", padding: "0.55rem 0.9rem", fontWeight: 600, fontSize: "0.85rem", cursor: working ? "default" : "pointer" }}>
            Cancel
          </button>
          <button type="button" onClick={onSubmit} disabled={!canSubmit}
            style={{ background: canSubmit ? BRAND.gold : BRAND.raised, color: canSubmit ? BRAND.dark : BRAND.muted, border: "none", borderRadius: "8px", padding: "0.6rem 1.1rem", fontWeight: 800, fontSize: "0.9rem", cursor: canSubmit ? "pointer" : "default" }}>
            {working ? "Working…" : "Upload to gallery"}
          </button>
        </div>
      </div>
    </div>
  );
}

function L({ label, children }) {
  return (
    <div style={{ marginBottom: "0.7rem" }}>
      <div style={{ color: BRAND.muted, fontSize: "0.72rem", fontWeight: 700, letterSpacing: "0.03em", marginBottom: "0.25rem", textTransform: "uppercase" }}>{label}</div>
      {children}
    </div>
  );
}

function Grid2({ children }) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.6rem" }}>
      {children}
    </div>
  );
}

const inputStyle = {
  background: BRAND.panel,
  border: `1px solid ${BRAND.border}`,
  borderRadius: "6px",
  color: "white",
  padding: "0.5rem 0.65rem",
  fontSize: "0.88rem",
  width: "100%",
  boxSizing: "border-box",
  outline: "none",
  fontFamily: "inherit",
};
